import { HeaderData } from './header.class';
import { TimePeriodData } from './time-period.class';
import { ChoiceData, ChoiceEnum } from './vote/choice.class';

/**
 * Helpers for reading poll data as it is persisted (whoo/header, whoo/options, whoo/votes) - shared by the vote
 * confirmation mail renderer (functions/src/whocan/mail/vote-renderer.ts) and the calendar export
 * (poll/calendar/calendar-events.ts), so the two cannot drift apart on what is a date, which choice an answer
 * holds and when the header date and location are shown (calendar quick wins B, task 4). Since package C the vote
 * page reads the location through headerLocation too.
 * Pure: nothing from Angular, Firebase or the browser.
 */

/** the kind of secondary answer an option asks for */
export type SecondaryAnswerKind = 'none' | 'text' | 'textAndNumber' | 'number' | 'slider' | 'checkboxes' | 'radio' | 'dropdown';

export interface OptionShape {
    isDate: boolean;
    /** the choice (YES/NO/MAYBE) is a real answer the voter gives - not for questions that only have secondary answers */
    hasPrimaryChoice: boolean;
    secondary: SecondaryAnswerKind;
}

/**
 * What an option is, from its persisted config key - mirrors optionConfigMap in option/option.configuration.ts
 * without depending on OptionConfigClass. Legacy options without a config key go by their type, like the
 * OptionClass constructor (OptionTypeEnum values "DATE", "TEXT", "TEXT-NUMBER").
 */
export function optionShape(optionConfigKey: string | undefined, type: string | undefined): OptionShape {
    switch (optionConfigKey) {
        case 'date': return { isDate: true, hasPrimaryChoice: true, secondary: 'none' };
        case 'date_willJoin': return { isDate: true, hasPrimaryChoice: true, secondary: 'none' };
        case 'text': return { isDate: false, hasPrimaryChoice: true, secondary: 'none' };
        case 'text_text': return { isDate: false, hasPrimaryChoice: false, secondary: 'text' };
        case 'text_slider': return { isDate: false, hasPrimaryChoice: false, secondary: 'slider' };
        case 'text_number': return { isDate: false, hasPrimaryChoice: false, secondary: 'number' };
        case 'textAndNumber_textAndNumber':
        case 'text_textAndNumber': return { isDate: false, hasPrimaryChoice: false, secondary: 'textAndNumber' };
        case 'checkboxes': return { isDate: false, hasPrimaryChoice: false, secondary: 'checkboxes' };
        case 'radio': return { isDate: false, hasPrimaryChoice: false, secondary: 'radio' };
        case 'dropdown': return { isDate: false, hasPrimaryChoice: false, secondary: 'dropdown' };
    }
    if (type === 'DATE') return { isDate: true, hasPrimaryChoice: true, secondary: 'none' };
    if (type === 'TEXT-NUMBER') return { isDate: false, hasPrimaryChoice: false, secondary: 'textAndNumber' };
    return { isDate: false, hasPrimaryChoice: true, secondary: 'none' };
}

/** firebase hands sparse numeric nodes back as a map - normalise to a dense array by numeric key */
export function toArray<T>(value: T[] | Record<string, T> | null | undefined): (T | undefined)[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    const result: (T | undefined)[] = [];
    for (const key of Object.keys(value)) {
        const index = Number(key);
        if (Number.isInteger(index) && index >= 0) result[index] = value[key];
    }
    return result;
}

/** a persisted choice - the plain enum string of old votes or the object form - as ChoiceData */
export function toChoiceData(choice: ChoiceEnum | ChoiceData | undefined | null): ChoiceData {
    if (!choice) return { choiceEnum: ChoiceEnum.notSelected };
    if (typeof choice === 'string') return { choiceEnum: choice as ChoiceEnum };
    return { ...choice, choiceEnum: choice.choiceEnum ?? ChoiceEnum.notSelected };
}

export function isBlank(value: unknown): boolean {
    return value === undefined || value === null || String(value).trim() === '';
}

/** whether the persisted time period carries a usable date at all (startString, or the legacy date) */
export function hasTimePeriod(data: TimePeriodData | null | undefined): boolean {
    if (!data) return false;
    const anyData = data as { startString?: string; date?: string };
    return !isBlank(anyData.startString) || !isBlank(anyData.date);
}

/** the header date is shown: showWhen or showWhenAdvanced, and a stored date */
export function isHeaderWhenVisible(header: HeaderData | null | undefined): boolean {
    if (!header) return false;
    return !!(header.showWhen || header.showWhenAdvanced) && hasTimePeriod(header.when);
}

/**
 * Whether the poll template puts a header setting behind a toggle of the advanced section (availableSettings
 * locationAdvanced / meetingLinkAdvanced in template.config.ts, resolved along inheritFrom). The lib does not know
 * template.config.ts: the app reads it from TemplateService, the functions from the label generator
 * (functions/src/locale/vote-mail-labels.generated.ts, voteMailTemplateSettingFlags).
 */
export interface HeaderSettingVisibility {
    hasAdvancedToggle: boolean;
}

/** the template flags the header visibility depends on - the input of calendarEventsFor and the mail renderer */
export interface TemplateSettingFlags {
    /** the location sits behind showLocationAdvanced */
    locationAdvanced: boolean;
    /** the meeting link sits behind showMeetingLinkAdvanced */
    meetingLinkAdvanced: boolean;
    /**
     * the template offers the meeting link at all (meetingLinkFirstStep, meetingLink or meetingLinkAdvanced) - not the
     * invitation, potluck and duties families (Kai 2026-11-02, calendar quick wins C2): a link stored there is not shown
     */
    meetingLinkAvailable: boolean;
}

/**
 * The location as shown on the vote page (calendar quick wins C, task 0) - the one rule for the vote page, the
 * calendar export and the vote confirmation mail: a text, and either the poll template has no location toggle
 * (field in the first step or the main settings: invitation, potluck) or showLocationAdvanced is on. Trimmed;
 * undefined otherwise. The legacy flag showLocation does not count - no app sets it any more, and with it the
 * toggle of an old header could not hide the location.
 */
export function headerLocation(header: HeaderData | null | undefined, visibility: HeaderSettingVisibility): string | undefined {
    if (!header || isBlank(header.location)) return undefined;
    if (visibility.hasAdvancedToggle && !header.showLocationAdvanced) return undefined;
    return String(header.location).trim();
}
